import { Box, Button, IconButton, Paper, Typography } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  Add,
  Check,
  DeleteForever,
  Lock,
  LockOpen,
  Remove,
  Settings,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import ActionButton from '../../../components/ActionButton';
import CustomTooltip from '../../../components/CustomTooltip';
import CustomTable from '../components/CustomTable';
import EditableCell from '../components/EditableCell';
import EditableDateCell from '../components/EditableDateCell';

const useStyles = makeStyles((theme) => ({
  paper: {
    width: '100%',
    padding: theme.spacing(4, 8),
    boxSizing: 'border-box',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    // gap: theme.spacing(2),
  },
  title: {
    fontSize: 30,
  },
  actionWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  addButton: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  centeredCellContent: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
}));

const ProcedureAutocontrolloTable = ({
  addNewRow,
  aggiornamentiGestioneOperativaId,
  consulenteId,
  esercizioId,
  handleCellChange,
  handleSave,
  loading,
  onRemoveRowProcedure,
  procedureAutocontrollo,
  isEditingDisabledForConsulente,
  onToggleEditingForConsulente,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();

  const isConsulente =
    localStorage.getItem('superconsulente') === 'false' &&
    localStorage.getItem('consulenteId') !== 'null';

  const isEditingBlocked =
    isConsulente &&
    isEditingDisabledForConsulente.procedureHaccp &&
    isEditingDisabledForConsulente.schedeAutocontrollo;

  const columns = [
    {
      Header: 'Titolo',
      id: 'titoloProcedureAutocontrollo',
      minWidth: 240,
      accessor: (d) => d.titolo,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableCell
            value={props.value}
            disabled={isEditingBlocked}
            onChange={(newValue) =>
              handleCellChange(props.index, 'titolo', newValue)
            }
          />
        </div>
      ),
    },
    {
      Header: 'Versione',
      id: 'numVersioneProcedureAutocontrollo',
      width: 120,
      accessor: (d) => d.numVersione,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableCell
            value={props.value}
            disabled={isEditingBlocked}
            onChange={(newValue) =>
              handleCellChange(props.index, 'numVersione', newValue)
            }
          />
        </div>
      ),
    },
    {
      Header: 'Data',
      id: 'dataVersioneProcedureAutocontrollo',
      width: 160,
      accessor: (d) => d.dataVersione,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <EditableDateCell
            value={props.value}
            disabled={isEditingBlocked}
            onChange={(newValue) =>
              handleCellChange(props.index, 'dataVersione', newValue)
            }
          />
        </div>
      ),
    },
    {
      Header: 'Controllo',
      id: 'controlloProcedureAutocontrollo',
      width: 120,
      accessor: (d) => d,
      Cell: (props) => {
        const isScheda = props.value.isScheda;

        return (
          <div className={classes.centeredCellContent}>
            {!isScheda ? (
              <Check
                titleAccess="Procedura di controllo"
                style={{
                  color: theme.palette.text.primary,
                  fontSize: 30,
                  cursor: 'not-allowed',
                }}
              />
            ) : (
              <Remove
                titleAccess="Non è una procedura di controllo"
                style={{
                  color: theme.palette.action.disabled,
                  fontSize: 30,
                  cursor: 'not-allowed',
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      Header: 'Scheda',
      id: 'schedaProcedureAutocontrollo',
      width: 120,
      accessor: (d) => d,
      Cell: (props) => {
        const isScheda = props.value.isScheda;

        return (
          <div className={classes.centeredCellContent}>
            {isScheda ? (
              <Check
                titleAccess="Scheda di registrazione"
                style={{
                  color: theme.palette.text.primary,
                  fontSize: 30,
                  cursor: 'not-allowed',
                }}
              />
            ) : (
              <Remove
                titleAccess="Non è una scheda di registrazione"
                style={{
                  color: theme.palette.action.disabled,
                  fontSize: 30,
                  cursor: 'not-allowed',
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      Header: 'Modifica',
      id: 'modificaProcedureAutocontrollo',
      width: 180,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => {
        const isRowDisabled = props.value.id.toString().startsWith('temp-');

        const handleClick = () => {
          const { id, isScheda } = props.value;
          const path = isScheda ? 'scheda' : 'procedura';
          history.push(
            `/amministrazione/esercizi_commerciali/${esercizioId}/${path}/${id}`,
            { aggiornamentiGestioneOperativaId, consulenteId },
          );
        };

        const iconButton = (
          <span className={classes.centeredCellContent}>
            <IconButton
              disabled={isRowDisabled || isEditingBlocked}
              onClick={handleClick}
            >
              <Settings
                style={{
                  color:
                    isRowDisabled || isEditingBlocked
                      ? theme.palette.action.disabled
                      : theme.palette.primary.main,
                }}
              />
            </IconButton>
          </span>
        );

        return isRowDisabled ? (
          <CustomTooltip
            title={
              <Typography variant="caption">
                Salva per accedere al dettaglio
              </Typography>
            }
            placement="top"
            arrow
          >
            {iconButton}
          </CustomTooltip>
        ) : (
          iconButton
        );
      },
    },
    {
      Header: 'Elimina',
      id: 'eliminaProcedureAutocontrollo',
      width: 120,
      sortable: false,
      accessor: (d) => d,
      Cell: (props) => (
        <div className={classes.centeredCellContent}>
          <IconButton
            onClick={() => onRemoveRowProcedure(props)}
            disabled={isEditingBlocked}
            style={{
              color: isEditingBlocked
                ? theme.palette.disabled.main
                : theme.palette.primary.main,
            }}
          >
            <DeleteForever />
          </IconButton>
        </div>
      ),
    },
  ];

  return (
    <Paper className={classes.paper}>
      <div className={classes.titleWrapper}>
        <Typography variant="h5" className={classes.title}>
          Procedure autocontrollo
        </Typography>
        <div className={classes.actionWrapper}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={() => addNewRow('procedureAutocontrollo')}
            title="Aggiungi procedura"
            disabled={isEditingBlocked}
            className={classes.addButton}
          >
            Procedura
          </Button>

          <IconButton
            disabled={isConsulente}
            onClick={() => {
              onToggleEditingForConsulente('procedureHaccp');
              onToggleEditingForConsulente('schedeAutocontrollo');
            }}
          >
            {isEditingDisabledForConsulente.procedureHaccp &&
            isEditingDisabledForConsulente.schedeAutocontrollo ? (
              <Lock titleAccess="Modifica bloccata" />
            ) : (
              <LockOpen titleAccess="Modifica consentita" />
            )}
          </IconButton>
        </div>
      </div>

      <CustomTable
        records={procedureAutocontrollo}
        columns={columns}
        defaultPageSize={10}
        loading={loading}
        filterable={false}
        resizable={true}
        sortable={true}
      />
      <Box mt={3} display="flex" justifyContent="flex-end">
        <ActionButton
          label="Salva modifiche"
          onClick={handleSave}
          disabled={isEditingBlocked}
        />
      </Box>
    </Paper>
  );
};

ProcedureAutocontrolloTable.propTypes = {
  addNewRow: PropTypes.func.isRequired,
  aggiornamentiGestioneOperativaId: PropTypes.number.isRequired,
  consulenteId: PropTypes.number.isRequired,
  esercizioId: PropTypes.string.isRequired,
  handleCellChange: PropTypes.func.isRequired,
  handleSave: PropTypes.func.isRequired,
  isEditingDisabledForConsulente: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  onRemoveRowProcedure: PropTypes.func.isRequired,
  onToggleEditingForConsulente: PropTypes.func.isRequired,
  procedureAutocontrollo: PropTypes.array.isRequired,
};

export default ProcedureAutocontrolloTable;
